<script setup lang="ts">
import type { BreadcrumbItem } from '~/components/ui/breadcrumb';
import type { AboutPage, WithContext } from 'schema-dts';

import { defaultTwitterCardMeta } from '@/constants/seo';
import { createPopup as typeformCreatePopup } from '@typeform/embed';
import { useEvents } from '~/composables/events/useEvents';
import { useAbsoluteUrl } from '~/composables/useAbsoluteUrl';
import messages from '~~/i18n/locale/pt_br/about';

const { toAbsoluteUrl } = useAbsoluteUrl();
const ogImageUrl = toAbsoluteUrl(
	'/images/social/openGraph/og_sobre_a_pilar.jpg'
);
useSeoMeta({
	title: 'Sobre a Pilar | PilarHomes',
	description:
		'Saiba mais sobre quem está por trás do PilarHomes. Líder em vendas no alto padrão de São Paulo, a Pilar veio para transformar o mercado imobiliário brasileiro.',
	keywords: 'PilarHomes, Pilar, alto padrão, São Paulo, mercado imobiliário',
	ogTitle: 'Sobre a Pilar | PilarHomes',
	ogDescription:
		'Saiba mais sobre quem está por trás do PilarHomes. Líder em vendas no alto padrão de São Paulo, a Pilar veio para transformar o mercado imobiliário brasileiro.',
	ogImage: ogImageUrl,
	twitterCard: defaultTwitterCardMeta,
	twitterTitle: 'Sobre a Pilar | PilarHomes',
	twitterDescription:
		'Saiba mais sobre quem está por trás do PilarHomes. Líder em vendas no alto padrão de São Paulo, a Pilar veio para transformar o mercado imobiliário brasileiro.',
	twitterImage: ogImageUrl
});

definePageMeta({
	layout: 'default-transparent'
});

useJsonld(
	() =>
		({
			'@type': 'AboutPage',
			'@context': 'https://schema.org',
			name: 'Sobre a Pilar',
			description:
				'Saiba mais sobre quem está por trás do PilarHomes. Líder em vendas no alto padrão de São Paulo, a Pilar veio para transformar o mercado imobiliário brasileiro.',
			about: {
				'@id': 'pilar-organization'
			}
		}) satisfies WithContext<AboutPage>
);

const typeformPopup = ref<ReturnType<typeof typeformCreatePopup>>();
const clickCount = ref(0);
const { wantToBecomeABrokerTypeformId } = useRuntimeConfig().public;
const { user } = useUserSession();
const { trackPageView, trackCtaViewMoreClicked, trackFormBrokerClicked } =
	useEvents();
const { t, tm } = useI18n({
	useScope: 'local',
	messages
});

const handleClick = () => {
	clickCount.value++;
};

onMounted(() => {
	typeformPopup.value = typeformCreatePopup(wantToBecomeABrokerTypeformId);
	document.addEventListener('click', handleClick);
	trackPageView({
		current_page: 'Sobre a Pilar',
		previous_page_url: document.referrer
	});
});

onBeforeUnmount(() => {
	document.removeEventListener('click', handleClick);
});

const handleOpenTypeform = () => {
	typeformPopup.value?.open();
	trackFormBrokerClicked({
		current_page: 'Sobre a Pilar',
		section_name: 'page content',
		cta_text: 'Quero ser um corretor Pilar',
		user_name: user.value?.name ?? undefined,
		user_email: user.value?.email ?? undefined,
		user_phone: user.value?.phoneNumber ?? undefined
	});
};

const handleSearchProperties = () => {
	trackCtaViewMoreClicked({
		current_page: 'Sobre a Pilar',
		section_name: 'page content',
		cta_text: 'Buscar imóveis',
		destination_url: '/venda/imoveis/sao-paulo-sp-brasil',
		click_count: clickCount.value
	});
};

const breadcrumbItems: BreadcrumbItem[] = [
	{
		label: 'Início',
		route: '/'
	},
	{
		label: 'Sobre a Pilar',
		route: '/sobre-a-pilar'
	}
];
</script>

<template>
	<article class="overflow-hidden">
		<LazyAboutHeader
			:title="t('header.title')"
			:description="t('header.description')"
		/>

		<LazyAboutHistory
			:title="t('sections.ourHistory.title')"
			:paragraphs="tm('sections.ourHistory.paragraphs')"
		>
			<template #breadcrumb>
				<Breadcrumb :items="breadcrumbItems" />
			</template>
		</LazyAboutHistory>

		<LazyAboutResults
			:title="{
				sectionTitle: tm('sections.results.title'),
				vgv: tm('sections.results.vgv.title'),
				highValueSales: tm('sections.results.highValueSales.title')
			}"
			:description="{
				vgv: t('sections.results.vgv.description'),
				vgvNote: t('sections.results.vgv.note'),
				highValueSales: t('sections.results.highValueSales.description')
			}"
			:areas="tm('sections.results.highValueSales.areas')"
		/>

		<LazyAboutBrokers
			:title="tm('sections.brokers.title')"
			:cta="t('sections.brokers.cta')"
			@open-typeform="handleOpenTypeform"
		/>

		<LazyAboutNaturalEvolution
			:title="t('sections.naturalEvolution.title')"
			:description="t('sections.naturalEvolution.description')"
			:cta="t('sections.naturalEvolution.cta')"
			@search-properties="handleSearchProperties"
		/>

		<LazyAboutExperience
			:title="t('sections.experience.title')"
			:description="t('sections.experience.description')"
			:features="tm('sections.experience.features')"
			:cta="t('sections.experience.cta')"
			@search-properties="handleSearchProperties"
		/>
	</article>
</template>
